import { useState } from 'react';
import * as Yup from 'yup';

export interface UseFieldValidationReturn<T extends Record<string, any>> {
  errors: Record<keyof T, boolean>;
  validateAllFields: (values: T) => boolean;
  handleFieldChange: (fieldName: keyof T, value: any) => void;
}

/**
 * Simplified field validation hook that follows the pattern from NoATSJobDescriptionStep.tsx
 * Uses Yup schema for validation and provides individual field error states with full TypeScript typing
 */
export const useFieldValidation = <T extends Record<string, any>>(
  schema: Yup.ObjectSchema<T>
): UseFieldValidationReturn<T> => {
  // Initialize errors state based on schema fields with proper typing
  const [errors, setErrors] = useState<Record<keyof T, boolean>>(() => {
    const initialErrors = {} as Record<keyof T, boolean>;
    Object.keys(schema.fields).forEach((fieldName) => {
      initialErrors[fieldName as keyof T] = false;
    });
    return initialErrors;
  });

  const validateAllFields = (values: T): boolean => {
    let hasErrors = false;
    const newErrors = {} as Record<keyof T, boolean>;

    // Initialize all errors to false
    Object.keys(schema.fields).forEach((fieldName) => {
      newErrors[fieldName as keyof T] = false;
    });

    // Validate each field individually
    Object.keys(schema.fields).forEach((fieldName) => {
      try {
        const fieldSchema = schema.fields[fieldName] as Yup.Schema;
        fieldSchema.validateSync(values[fieldName as keyof T]);
      } catch (error) {
        newErrors[fieldName as keyof T] = true;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleFieldChange = (fieldName: keyof T, value: any) => {
    // Clear error when user starts typing and field becomes valid
    if (errors[fieldName]) {
      try {
        const fieldSchema = schema.fields[fieldName as string] as Yup.Schema;
        fieldSchema.validateSync(value);
        setErrors((prev) => ({
          ...prev,
          [fieldName]: false
        }));
      } catch {
        // Keep error state if still invalid
      }
    }
  };

  return {
    errors,
    validateAllFields,
    handleFieldChange
  };
};
